import { useCallback, useEffect, useMemo, useState } from "react";
// Material UI
import { GridCellParams, GridInitialState, useGridApiRef } from "@mui/x-data-grid-premium";
import { Stack, SxProps } from "@mui/material";

// Context
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { SalesPerformanceTableTitle } from "../components/SalesPerformanceTableTitle";

// Hooks
import { getSalesPerformanceRowsWithSearchValues, useSalesPerformanceTableColumns } from "../hooks/useSalesPerformanceTableConfiguration";

// Constants
import { DEFAULT_HEADER_HEIGHT } from "@/Washouts/constants";
import { tableKeys } from "@/Constants/tableKeys";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Utils
import { generateSx, getRowClassName, getRowsFromFilteredLookUp } from "@/Layouts/components/Tables/DataSmartTable/helpers/";
import { useLocation } from "@Common/hooks/Location/useLocation.tsx";

// Types
import { DataSmartTableProps } from "@/Layouts/components/types";
import { ISaleIdentifier, ISales, ISalesPerformance } from "@DataPages/types/salesOperations/salesPerformance/models/salesPerformance.ts";

const getCellClassName = (params: GridCellParams) => {
	// Targeting footer
	const isFooter = params.id === "auto-generated-group-footer-root";
	if (!isFooter) {
		return "";
	}

	// Use this to change the cell class name depending on the cell value
	const negatives = ["amountFinanced"];
	if (negatives.includes(params.field) && (params.value as number) < 0) {
		return "aggregation-footer-negative";
	}

	// Append label 'Total' to the cell value in footer
	if (params.field === "date") {
		return "aggregation-footer-total";
	}

	if (params.field === "dealNumber") {
		return "aggregation-footer-negative";
	}

	return "";
};

// Constant configurations
const pinnedColumns = {
	left: [
		"_id",
		"funded",
		"external",
		"sourceLocationId",
		"salesEmployeeName",
		"saleDate",
		"dealNumber",
		"frontGrossProfit",
		"backGrossProfit",
		"__empty",
	],
};

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

const customProps: DataSmartTableProps["customProps"] = {
	tableContainerClassName: "[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
	tableClassName: {
		"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
		"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
		"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
	},
	aggregationFooterClassName: "aggregation-footer regular-footer",
	expandableRows: false,
	hide: false,
};

const sumTotalValues = (rows: ISales[], key: keyof ISales) => {
	return rows.reduce((accumulator, currentValue) => accumulator + (currentValue[key] as number), 0);
};

const columnVisibilityModel = {
	_id: false,
	salesEmployeeId: false,
	financeEmployeeId: false,
	salesManagerId: false,
	sourceLocationId: false,
};

const initialState: GridInitialState = {
	aggregation: {
		model: {
			_id: "size",
		},
	},
};

interface ISalesPerformanceTableProps {
	rows: ISalesPerformance[] | ISales[];
	useSwitch?: boolean;
	operational?: boolean;
	locationId: string;
	selectedOems?: { dealerCodeFS: string; oemName: string }[];
	identifiers?: ISaleIdentifier[];
	tableKey: string;
	// Callbacks
	onSelectOEM?: (values: unknown[]) => void;
	onClear?: () => void;
	onUseSwitch?: (value: boolean) => void;
}

type TProductsFooter = {
	[key: string]: number;
};

const calculateProductFooters = (rows: ISales[]): TProductsFooter => {
	const productsFootersCalculation: TProductsFooter = {};
	rows.forEach((row) => {
		row?.frontProducts?.forEach?.((product) => {
			if (!productsFootersCalculation[product.product]) {
				productsFootersCalculation[product.product] = 0;
			}
			productsFootersCalculation[product.product] += product.value;
		});
		row?.backProducts?.forEach?.((product) => {
			if (!productsFootersCalculation[product.product]) {
				productsFootersCalculation[product.product] = 0;
			}
			productsFootersCalculation[product.product] += product.value;
		});
	});
	return productsFootersCalculation;
};

export default function SalesPerformanceTable(props: Readonly<ISalesPerformanceTableProps>) {
	const location = useLocation();
	const { locationId } = location ?? {};

	const apiRef = useGridApiRef();

	const { rows, operational, useSwitch, onUseSwitch, onSelectOEM, selectedOems, identifiers = [] } = props;
	const initialFooters = {
		frontGrossProfit: sumTotalValues(rows || [], "frontGrossProfit"),
		backGrossProfit: sumTotalValues(rows || [], "backGrossProfit"),
		units: sumTotalValues(rows || [], "units"),
	};

	const [totalFrontGrossProfitFooter, setTotalFrontGrossProfitFooter] = useState(initialFooters.frontGrossProfit);
	const [totalBackGrossProfitFooter, setTotalBackGrossProfitFooter] = useState(initialFooters.backGrossProfit);
	const [totalUnitsFooter, setTotalUnitsFooter] = useState(initialFooters.units);

	const [productsFooters, setProductsFooters] = useState<TProductsFooter>(calculateProductFooters(rows || []));

	const { columns, groupingColDef } = useSalesPerformanceTableColumns({
		rows,
		totalFrontGrossProfitFooter,
		totalBackGrossProfitFooter,
		totalUnitsFooter,
		productsFooters,
		locationId,
	});

	const onStateChange = useCallback(() => {
		if (!apiRef.current) {
			return;
		}

		const state = apiRef.current.state.filter;
		const rowModels = apiRef.current.state.rows.dataRowIdToModelLookup;
		const filtered = state.filteredRowsLookup || null;

		const rowsAfterFilter = getRowsFromFilteredLookUp(filtered, rowModels) as ISales[];

		setTotalBackGrossProfitFooter(sumTotalValues(rowsAfterFilter, "backGrossProfit"));
		setTotalFrontGrossProfitFooter(sumTotalValues(rowsAfterFilter, "frontGrossProfit"));
		setTotalUnitsFooter(sumTotalValues(rowsAfterFilter, "units"));
		setProductsFooters(calculateProductFooters(rowsAfterFilter));
	}, []);

	useEffect(() => {
		if (!rows?.length || !apiRef.current?.subscribeEvent) {
			return () => {};
		}

		const clearListener1 = apiRef.current?.subscribeEvent("filterModelChange", onStateChange);
		const clearListener2 = apiRef.current?.subscribeEvent("rowCountChange", onStateChange);

		onStateChange();
		return () => {
			clearListener1();
			clearListener2();
		};
	}, [rows]);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				titleExpandible: {
					className: "mt-[0px] [&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
					hide: true,
				},
				customComponent: (
					<Stack direction="row" alignItems="center" sx={{ height: "58px" }}>
						<SalesPerformanceTableTitle
							isLoading={false}
							operational={operational}
							useSwitch={useSwitch}
							onUseSwitch={onUseSwitch}
						/>
						{useSwitch && (
							<SmartSelector
								multiple
								tagsVariant={"text"}
								showBy={"label"}
								selectBy={"dealerCodeFS"}
								options={identifiers}
								selected={selectedOems}
								inputProps={{
									placeholder: operational ? "Select OEMs" : "",
									classes: "[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px]",
								}}
								listBoxProps={{
									sx: { maxHeight: "220px" },
								}}
								clearable
								disabled={!operational}
								sx={{ width: 200, mt: "8px", height: 30 }}
								onChangeAction={(values) => {
									const selections = values as unknown[];
									onSelectOEM?.(selections);
								}}
							/>
						)}
					</Stack>
				),
				filter: {
					data: rows || [],
					hide: false,
					disabled: false,
				},
				search: {
					useQuickFilter: true,
					data: getSalesPerformanceRowsWithSearchValues(rows),
				},
				expand: {
					hide: true,
				},
			},
			noRowsOverlay: {
				title: "No Sales Performance Data",
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: "No Sales Performance Data",
				className: "!text-montserrat",
			},
		}),
		[rows, operational, selectedOems]
	);

	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		setSx(generateSx(apiRef, {}));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef, {}));
		});
	}, [props, apiRef]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: `${tableKeys.DATA_PAGES.SALES_PERFORMANCE}/${props.tableKey}`,
			}}
			apiRef={apiRef}
			columns={columns}
			rows={rows || []}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			maxRows={15}
			pinnedColumns={pinnedColumns}
			getRowClassName={getRowClassName}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={columnVisibilityModel}
			slots={slots}
			slotProps={slotProps}
			hideFooter
			initialState={initialState}
			groupingColDef={groupingColDef}
			getCellClassName={getCellClassName}
			customProps={customProps}
			sx={sx}
			rowHeight={25}
			autoHeight={false}
		/>
	);
}
